import {DateOnly} from "@microsoft/kiota-abstractions";

export const DATEFORMAT = 'yy-mm-dd';

export function dateFormatter(params: { value?: string | number | Date | null } | string | number | Date | null | undefined) {
  if (!params) return {};

  const value = params instanceof Date
    ? params.getTime()
    : typeof params === 'object' && params !== null
      ? params.value
      : params;
  const timestamp = new Date(value ?? '');

  if (!timestamp || Number.isNaN(timestamp.getTime())) {
    return {};
  }

  const year = timestamp.getFullYear();
  const month = String(timestamp.getMonth() + 1).padStart(2, '0');
  const day = String(timestamp.getDate()).padStart(2, '0');
  const hours = String(timestamp.getHours()).padStart(2, '0');
  const minutes = String(timestamp.getMinutes()).padStart(2, '0');
  const seconds = String(timestamp.getMinutes()).padStart(2, '0');

  const dayOfWeek = timestamp.getDay() === 0 ? 7 : timestamp.getDay();

  return {
    day: dayOfWeek,
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}:${seconds}`,
    timeWithoutSeconds: `${hours}:${minutes}`,
    timestamp: `${year}${month}${day}${hours}${minutes}${seconds}`,
    dateOnly: DateOnly.fromDate(timestamp)
  };
};

export function toDateOnly(value: unknown): DateOnly | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return null;
  }

  if (value instanceof DateOnly) {
    return value;
  }

  if (value instanceof Date) {
    return DateOnly.fromDate(value);
  }

  if (typeof value === 'string') {
    return DateOnly.parse(value) ?? null;
  }

  return undefined;
}

export function toDateOnlyString(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && value && 'toString' in value && typeof value.toString === 'function') {
    const stringValue = value.toString();
    return stringValue === '[object Object]' ? null : stringValue;
  }

  return null;
}

/**
 * Formats a date value to DD/MM/YYYY format
 * @param value - Date object, ISO string, or null/undefined
 * @returns Formatted date string or empty string if invalid
 * @example
 * formatDate(new Date('2024-01-15')) // '15/01/2024'
 * formatDate('2024-01-15T10:30:00') // '15/01/2024'
 * formatDate(null) // ''
 */
export function formatDate(value?: Date | string | null): string {
  if (!value) return ''

  const date = new Date(value)
  if (isNaN(date.getTime())) return ''

  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()

  return `${day}/${month}/${year}`
}

/**
 * Formats a date value to DD/MM/YYYY HH:mm format
 * @param value - Date object, ISO string, or null/undefined
 * @returns Formatted datetime string or empty string if invalid
 * @example
 * formatDateTime(new Date('2024-01-15T14:30:00')) // '15/01/2024 14:30'
 * formatDateTime('2024-01-15T14:30:00') // '15/01/2024 14:30'
 * formatDateTime(null) // ''
 */
export function formatDateTime(value?: Date | string | null): string {
  if (!value) return ''

  const date = new Date(value)
  if (isNaN(date.getTime())) return ''

  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')

  return `${day}/${month}/${year} ${hours}:${minutes}`
}
